import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer";
import { aiFeatures } from "./featuresData";

// --- ★ FIXED StickyTabNav (Removed invalid 'jsx' attribute logic) ★ ---
const StickyTabNav = ({ features, activeTab, onTabClick, mainNavHeight }) => {
  const containerRef = useRef(null);
  const activeTabRef = useRef(null);

  const [isSticky, setIsSticky] = useState(false);
  const [originalTop, setOriginalTop] = useState(0);
  const [navHeight, setNavHeight] = useState(0);

  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current) {
        const top = containerRef.current.offsetTop;
        const height = containerRef.current.offsetHeight;
        setOriginalTop((prev) => (Math.abs(top - prev) > 5 ? top : prev));
        setNavHeight((prev) =>
          height > 0 && Math.abs(height - prev) > 2 ? height : prev,
        );
      }
    };

    // Fix for ResizeObserver loop limit exceeded error
    const observerCallback = () => {
      window.requestAnimationFrame(() => {
        updatePosition();
      });
    };

    updatePosition();
    const timers = [200, 1000, 3000].map((t) => setTimeout(updatePosition, t));
    window.addEventListener("resize", updatePosition);

    const resizeObserver = new ResizeObserver(observerCallback);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    resizeObserver.observe(document.body);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", updatePosition);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const safeNavHeight = mainNavHeight || 0;
      if (window.scrollY < 10) {
        setIsSticky(false);
        return;
      }
      if (originalTop === 0) return;
      const threshold = originalTop - safeNavHeight;
      const shouldBeSticky = window.scrollY >= threshold;
      if (shouldBeSticky !== isSticky) {
        setIsSticky(shouldBeSticky);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [originalTop, isSticky, mainNavHeight]);

  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeTab]);

  const stickyStyles = {
    transform: "translateZ(0)",
  };

  if (isSticky) {
    stickyStyles.top = `${mainNavHeight || 0}px`;
  }

  return (
    <>
      {/* Standard React way to inject internal styles without causing 'jsx' errors */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `,
        }}
      />

      <div ref={containerRef} className="relative w-full z-20">
        <nav
          className={`transition-[background,box-shadow,padding] duration-300 ease-in-out w-full ${
            isSticky
              ? "fixed left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg"
              : "relative bg-white/95 shadow-none"
          }`}
          style={isSticky ? stickyStyles : {}}
        >
          <div className="container mx-auto max-w-6xl px-0">
            <div className="flex overflow-x-auto hide-scrollbar">
              {features.map((feature) => (
                <button
                  key={feature.id}
                  ref={activeTab === feature.id ? activeTabRef : null}
                  onClick={() => onTabClick(feature.id)}
                  className={`
                    relative px-6 md:px-8 text-left font-medium text-base
                    transition-colors duration-300 flex-1
                    flex items-start justify-start
                    min-w-[160px] 
                    py-2 h-20 
                    ${
                      activeTab === feature.id
                        ? "bg-gray-100 text-gray-800"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }
                  `}
                >
                  {feature.title}
                  {activeTab === feature.id && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-1.5 bg-green-900"
                      layoutId="underline"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </nav>
        {isSticky && <div style={{ height: `${navHeight || 80}px` }} />}
      </div>
    </>
  );
};

// --- Feature Section ---
const FeatureSection = React.forwardRef(({ feature }, ref) => (
  <div ref={ref} className="container mx-auto max-w-6xl px-4 pt-16 md:pt-24">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center min-h-[450px]">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
      >
        {feature.icon && <div className="mb-6">{feature.icon}</div>}
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          {feature.heading}
        </h2>
        <ul className="my-8 p-0 list-none">
          {feature.points.map((point, index) => (
            <li
              key={index}
              className="py-2 border-b border-gray-200 last:border-b-0"
            >
              <span className="text-gray-600 text-sm leading-relaxed font-medium block text-left">
                {point}
              </span>
            </li>
          ))}
        </ul>
        <Link
          to={`/solutions/ai-security-governance/${feature.id}`}
          className="inline-block bg-green-900  text-white font-semibold mt-2 text-lg px-6 py-2 rounded-md hover:bg-green-700 transition-colors no-underline"
        >
          More Information &rarr;
        </Link>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-center md:justify-end"
      >
        <div className="relative overflow-hidden rounded-2xl group">
          <img
            src={feature.imageUrl}
            alt={feature.heading}
            className="w-full h-auto max-w-md object-contain rounded-2xl transition-transform duration-700 ease-out group-hover:scale-[1.15]"
            draggable={false}
          />
        </div>
      </motion.div>
    </div>
  </div>
));

// --- MAIN PAGE COMPONENT ---
export default function AISecurityandGovernance() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeTab, setActiveTab] = useState(aiFeatures[0].id);
  const [mainNavHeight, setMainNavHeight] = useState(null);

  useEffect(() => {
    const updateNavHeight = () => {
      const mainNav = document.getElementById("main-navbar");
      if (mainNav) {
        setMainNavHeight(mainNav.offsetHeight);
      }
    };
    updateNavHeight();
    window.addEventListener("resize", updateNavHeight);
    return () => window.removeEventListener("resize", updateNavHeight);
  }, []);

  const sectionRefs = useRef(
    aiFeatures.reduce((acc, feature) => {
      acc[feature.id] = React.createRef();
      return acc;
    }, {}),
  );

  useEffect(() => {
    const safeHeight = mainNavHeight || 0;
    const offset = safeHeight + 100;

    const handleScroll = () => {
      let currentTab = activeTab;
      for (const feature of aiFeatures) {
        const ref = sectionRefs.current[feature.id];
        if (ref.current) {
          const { top, bottom } = ref.current.getBoundingClientRect();
          if (top <= offset && bottom >= offset) {
            currentTab = feature.id;
            break;
          }
        }
      }
      if (currentTab !== activeTab) {
        setActiveTab(currentTab);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeTab, mainNavHeight]);

  const handleTabClick = (id) => {
    const ref = sectionRefs.current[id];
    const safeHeight = mainNavHeight || 0;

    if (ref.current) {
      const y =
        ref.current.getBoundingClientRect().top +
        window.scrollY -
        safeHeight -
        80;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveTab(id);
    }
  };

  return (
    <>
      <Helmet>
        <title>AI Security & Governance Solutions | Synthera Security</title>

        <meta
          name="description"
          content="Secure AI adoption with AI Security & Governance solutions from Synthera Security. Discover, protect, govern, and manage AI risks across your organization."
        />

        <meta
          name="keywords"
          content="AI Security, AI Governance, Agentic AI Security, AI Risk Management, AI Compliance, AI Protection, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="AI Security & Governance Solutions | Synthera Security"
        />

        <meta
          property="og:description"
          content="Protect AI systems, govern AI usage, and manage AI risks with Synthera Security."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/solutions/ai-security-and-governance"
        />

        <meta property="og:type" content="website" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/solutions/ai-security-and-governance"
        />
      </Helmet>

      <div className="bg-white text-gray-900 overflow-hidden">
        {/* 1. HERO IMAGE SECTION */}
        <div className="w-full bg-[#032d28]">
          {/* Desktop */}
          <div className="hidden md:block">
            <img
              src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1781010407/ChatGPT_Image_Jun_9_2026_01_58_39_PM_szm5ek.png"
              alt="AI Security & Governance"
              className="w-full h-auto block"
            />
          </div>

          {/* Mobile */}
          {/* Mobile */}
          <div className="block md:hidden pt-16 overflow-hidden">
            <img
              src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1781010407/ChatGPT_Image_Jun_9_2026_01_58_39_PM_szm5ek.png"
              alt="AI Security & Governance"
              className="w-full h-auto block"
            />
          </div>
        </div>
        {/* ================= PREMIUM INTRO SECTION ================= */}
        <section className="bg-white pt-28 pb-32 px-4 relative overflow-hidden">
          <div className="max-w-6xl mx-auto text-center">
            {/* ================= TOP TITLE ================= */}
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-[40px] md:text-[52px] font-bold tracking-tight text-gray-900 mb-6 leading-[1.1]"
            >
              You can’t protect what you can’t see
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-gray-600 text-lg max-w-2xl mx-auto mb-20 leading-relaxed"
            >
              See what others miss. Command data and AI agents, everywhere. Get
              to production, faster and safer.
            </motion.p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-20 mb-32">
              {[
                {
                  title: "Detect AI",
                  desc: "Find hidden AI agents, shadow AI, and risks you don't even know exist. See what others miss.",
                  icon: "https://res.cloudinary.com/dpn3bkjba/image/upload/v1772432758/detect_af5qme.svg",
                },
                {
                  title: "Protect AI",
                  desc: "Govern all your AI agents with policy enforcement, access controls, and continuous compliance.",
                  icon: "https://res.cloudinary.com/dpn3bkjba/image/upload/v1772432758/protect_oxslgp.svg",
                },
                {
                  title: "Undo AI",
                  desc: "Roll back unintended actions, restore data states, and recover from AI-induced errors instantly.",
                  icon: "https://res.cloudinary.com/dpn3bkjba/image/upload/v1772432759/undo_eibrf3.svg",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2, duration: 0.6 }}
                  className="group text-center"
                >
                  {/* Icon */}
                  <div className="relative flex justify-center mb-10">
                    {/* Soft glow */}
                    <div className="absolute w-32 h-32 bg-emerald-500/10 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition duration-500" />

                    <img
                      src={item.icon}
                      alt={item.title}
                      className="w-[130px] h-[130px] object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  {/* Title */}
                  <h3 className="text-[20px] font-semibold text-gray-900 mb-3">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
            {/* ================= DIFFERENCE TITLE ================= */}
            {/* ================= DIFFERENCE SECTION ================= */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="max-w-5xl mx-auto mt-24"
            >
              {/* Title */}
              <div className="text-center mb-10">
                <p className="text-gray-500 text-sm tracking-[0.15em] uppercase mb-3">
                  The difference between
                </p>

                <h2 className="text-[38px] md:text-[50px] font-extrabold tracking-tight text-gray-900 leading-[1.1]">
                  AI chaos and AI under control
                </h2>
              </div>

              {/* Divider Line + Center Dot */}
              <div className="relative flex items-center justify-center mb-14">
                <div className="absolute w-full h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>

                <div className="relative w-3 h-3 bg-emerald-500 rounded-full shadow-[0_0_14px_rgba(16,185,129,0.8)]"></div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[15px]">
                  <thead>
                    <tr>
                      <th></th>
                      <th className="pb-6 text-gray-500 font-medium">
                        without
                      </th>
                      <th className="pb-6 font-semibold">
                        <span className="text-emerald-600">with</span> Synthera
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {[
                      [
                        "Visibility",
                        "Shadow AI and siloed views",
                        "Unified view of all AI agents & models",
                      ],
                      [
                        "Risk Insight",
                        "Vague sense of risk",
                        "Early risk detection & actionable intelligence",
                      ],
                      [
                        "Controls",
                        "Fragmented & coarse-grained",
                        "All your controls in one place",
                      ],
                      [
                        "Governance",
                        "Ad hoc, manual",
                        "Automated enforcement of enterprise standards",
                      ],
                      [
                        "AI Adoption",
                        "Difficulty scaling",
                        "Unleash agents safely",
                      ],
                    ].map((row, i) => (
                      <tr
                        key={i}
                        className="border-t border-gray-200 hover:bg-gray-50/60 transition-colors duration-300"
                      >
                        <td className="py-6 font-semibold text-gray-900 w-[28%]">
                          {row[0]}
                        </td>

                        <td className="py-6 text-gray-600 w-[36%]">{row[1]}</td>

                        <td className="py-6 text-gray-800 w-[36%] relative">
                          {/* subtle highlight */}
                          <span className="relative z-10">{row[2]}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        <StickyTabNav
          features={aiFeatures}
          activeTab={activeTab}
          onTabClick={handleTabClick}
          mainNavHeight={mainNavHeight}
        />

        <div className="pb-16 md:pb-24">
          {aiFeatures.map((feature, index) => (
            <React.Fragment key={feature.id}>
              {index > 0 && (
                <div className="container mx-auto max-w-6xl px-4">
                  <hr className="border-t-1 border-gray-900 " />
                </div>
              )}
              <FeatureSection
                feature={feature}
                ref={sectionRefs.current[feature.id]}
              />
            </React.Fragment>
          ))}
        </div>
        <Footer />
      </div>
    </>
  );
}
